import React, { useRef, useState } from "react";
import { Button, Grid, Typography, Tooltip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoIcon from "@mui/icons-material/Info";
import { Field, Form, Formik } from "formik";
import Swal from "sweetalert2";
import styles from "../styles/autorizationPayments.module.scss";
import { IExportedPackage } from "../types/exportedPackages.type";
import { formatCurrency } from "shared/utils";
import {
  generateUploadLinkPayment,
  generateUploadLinkResolutionDocument,
  uploadPaymentDocumentToBlob,
  uploadResolutionDocumentToBlob,
  registerSinglePayment,
  type RegisterSinglePaymentRequest,
} from "../../../shared/api/services";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_TEXT_LENGTH = 20;

export type PaymentInfoFormValues = {
  numeroRPC: string;
  numeroResolucion: string;
  documentoResolucion: File | null;
  documentoResolucionId?: number;
  td: string;
  rubro: string;
  fechaPago: string;
  documentoPago: File | null;
  documentoPagoId?: number;
};

type ModalCargarInformacionPagoProps = {
  onClose: () => void;
  onSuccess: () => void;
  packageData?: IExportedPackage;
  readOnlyData?: {
    numeroFacturas?: string;
    codigoPrestador?: string;
    valorPagado?: string;
    numeroCDP?: string;
    numeroRPC?: string;
  };
  electronicDocumentIds?: number[];
};

const initialValues: PaymentInfoFormValues = {
  numeroRPC: "",
  numeroResolucion: "",
  documentoResolucion: null,
  td: "",
  rubro: "",
  fechaPago: "",
  documentoPago: null,
};

export const ModalCargarInformacionPago: React.FC<
  ModalCargarInformacionPagoProps
> = ({
  onClose,
  onSuccess,
  packageData,
  readOnlyData,
  electronicDocumentIds,
}) => {
  const resolutionFileRef = useRef<HTMLInputElement | null>(null);
  const paymentFileRef = useRef<HTMLInputElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const readOnlyFields = {
    numeroFacturas: readOnlyData?.numeroFacturas || "",
    codigoPrestador: readOnlyData?.codigoPrestador || "",
    valorPagado: readOnlyData?.valorPagado || "",
    numeroCDP: readOnlyData?.numeroCDP || "",
    numeroRPC: readOnlyData?.numeroRPC || "",
  };
  const budgetCertificate = readOnlyFields.numeroCDP
    ? readOnlyFields.numeroCDP.split(",").map((item) => item.trim())
    : [];
  const budgetRegistration = readOnlyFields.numeroRPC
    ? readOnlyFields.numeroRPC.split(",").map((item) => item.trim())
    : [];
  const cdpError = budgetCertificate.join(",").length > MAX_TEXT_LENGTH
    ? `Los CDP del paquete no pueden superar ${MAX_TEXT_LENGTH} caracteres en total`
    : "";
  const rpcError = budgetRegistration.join(",").length > MAX_TEXT_LENGTH
    ? `Los RPC del paquete no pueden superar ${MAX_TEXT_LENGTH} caracteres en total`
    : "";

  const validate = (values: PaymentInfoFormValues) => {
    const currentErrors: Partial<Record<keyof PaymentInfoFormValues, string>> =
      {};

    if (!values.numeroResolucion?.trim()) {
      currentErrors.numeroResolucion = "El número de resolución es obligatorio";
    } else if (values.numeroResolucion.length > MAX_TEXT_LENGTH) {
      currentErrors.numeroResolucion = `El número de resolución debe tener máximo ${MAX_TEXT_LENGTH} caracteres`;
    }
    if (!values.td?.trim()) {
      currentErrors.td = "El TD es obligatorio";
    } else if (values.td.length > MAX_TEXT_LENGTH) {
      currentErrors.td = `El TD debe tener máximo ${MAX_TEXT_LENGTH} caracteres`;
    }
    if (!values.rubro?.trim()) {
      currentErrors.rubro = "El rubro es obligatorio";
    } else if (values.rubro.length > MAX_TEXT_LENGTH) {
      currentErrors.rubro = `El rubro debe tener máximo ${MAX_TEXT_LENGTH} caracteres`;
    }
    if (!values.fechaPago) {
      currentErrors.fechaPago = "La fecha de pago es obligatoria";
    }
    if (!values.documentoPago) {
      currentErrors.documentoPago = "El documento de pago es obligatorio";
    } else if (values.documentoPago.size > MAX_FILE_SIZE) {
      currentErrors.documentoPago = "El archivo no puede exceder 10 MB";
    }
    if (
      values.documentoResolucion &&
      values.documentoResolucion.size > MAX_FILE_SIZE
    ) {
      currentErrors.documentoResolucion = "El archivo no puede exceder 10 MB";
    }

    return currentErrors;
  };

  return (
    <div className={styles.container}>
      <Formik
        initialValues={initialValues}
        validate={validate}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={async (values) => {
          setIsSubmitting(true);
          if (cdpError || rpcError) {
            Swal.fire({ icon: "error", title: "Datos de pago inválidos", text: cdpError || rpcError });
            setIsSubmitting(false);
            return;
          }
          const consecutive = packageData?.paymentConsecutive?.trim();
          if (!consecutive) {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "No se pudo obtener el consecutivo de pago del paquete.",
              confirmButtonText: "Aceptar",
            });
            setIsSubmitting(false);
            return;
          }
          try {
            let resolutionDocumentId: number | undefined;
            let paymentDocumentId: number | undefined;

            // Upload resolution document if present
            if (values.documentoResolucion) {
              const uploadLinks = await generateUploadLinkResolutionDocument({
                fileName: [values.documentoResolucion.name],
              });

              if (uploadLinks && uploadLinks.length > 0) {
                const uploadSuccess = await uploadResolutionDocumentToBlob(
                  uploadLinks[0].link,
                  values.documentoResolucion,
                );

                if (uploadSuccess) {
                  resolutionDocumentId = uploadLinks[0].id;
                } else {
                  setIsSubmitting(false);
                  return;
                }
              }
            }

            // Upload payment document (required field)
            if (values.documentoPago) {
              const uploadLinks = await generateUploadLinkPayment({
                fileName: [values.documentoPago.name],
              });

              if (uploadLinks && uploadLinks.length > 0) {
                const uploadSuccess = await uploadPaymentDocumentToBlob(
                  uploadLinks[0].link,
                  values.documentoPago,
                );

                if (uploadSuccess) {
                  paymentDocumentId = uploadLinks[0].id;
                } else {
                  setIsSubmitting(false);
                  return;
                }
              }
            }

            // Prepare request data for API
            const requestData: RegisterSinglePaymentRequest = {
              consecutive,
              electronicDocumentIds: electronicDocumentIds || [],
              totalValue:
                parseFloat(readOnlyFields.valorPagado.replace(/,/g, "")) || 0,
              budgetCertificate,
              budgetRegistration,
              paymentResolution: values.numeroResolucion,
              resolutionAttachmentId: resolutionDocumentId,
              treasurySequence: values.td,
              paymentCategory: values.rubro,
              paymentAt: values.fechaPago,
              paymentAttachmentId: paymentDocumentId || 0,
            };

            await registerSinglePayment(requestData);

            Swal.fire({
              icon: "success",
              title: "¡Éxito!",
              text: `La información de pago se ha cargado correctamente para el consecutivo ${packageData?.paymentConsecutive || ""}`,
              confirmButtonText: "Aceptar",
            });

            onSuccess();
            onClose();
          } catch (error) {
            console.error("Error al cargar información de pago:", error);
            const message = (error as { response?: { data?: { message?: string } } })
              ?.response?.data?.message;
            Swal.fire({
              icon: "error",
              title: "No se pudo registrar el pago",
              text: message || "Intente nuevamente o contacte a soporte.",
            });
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form
            className={styles.modalRevertForm}
            data-testid="form-load-payment-info"
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sx={{ width: "100%" }}>
                <Typography
                  className={styles.modalRevertLabel}
                  sx={{
                    fontWeight: "700 !important",
                    fontSize: "0.9rem",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "4px",
                    marginBottom: "6px",
                    width: "100%",
                    display: "block",
                  }}
                >
                  Información del paquete
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label className={`${styles.modalRevertLabel} text-required`}>
                    Número de facturas
                  </label>
                  <input
                    type="text"
                    className={styles.modalRevertInput}
                    value={readOnlyFields.numeroFacturas}
                    readOnly
                    disabled
                    data-testid="input-numero-facturas"
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label className={`${styles.modalRevertLabel} text-required`}>
                    Código prestador
                  </label>
                  <input
                    type="text"
                    className={styles.modalRevertInput}
                    value={readOnlyFields.codigoPrestador}
                    readOnly
                    disabled
                    data-testid="input-codigo-prestador"
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label className={`${styles.modalRevertLabel} text-required`}>
                    Valor pagado
                  </label>
                  <input
                    type="text"
                    className={styles.modalRevertInput}
                    value={formatCurrency(
                      parseFloat(
                        readOnlyFields.valorPagado.replace(/,/g, ""),
                      ) || 0,
                    )}
                    readOnly
                    disabled
                    data-testid="input-valor-pagado"
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label className={`${styles.modalRevertLabel} text-required`}>
                    N° CDP
                  </label>
                  <input
                    type="text"
                    className={styles.modalRevertInput}
                    value={readOnlyFields.numeroCDP}
                    readOnly
                    disabled
                    data-testid="input-numero-cdp"
                  />
                  {cdpError && <span className={styles.modalRevertError} role="alert">{cdpError}</span>}
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label className={styles.modalRevertLabel}>N° RPC</label>
                  <input
                    type="text"
                    className={styles.modalRevertInput}
                    value={readOnlyFields.numeroRPC}
                    readOnly
                    disabled
                    data-testid="input-numero-rpc"
                  />
                  {rpcError && <span className={styles.modalRevertError} role="alert">{rpcError}</span>}
                </div>
              </Grid>

              <Grid item xs={12} sx={{ width: "100%" }}>
                <Typography
                  className={styles.modalRevertLabel}
                  sx={{
                    fontWeight: "700 !important",
                    fontSize: "0.9rem",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "4px",
                    marginBottom: "6px",
                    width: "100%",
                    display: "block",
                  }}
                >
                  Información de pago
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label
                    htmlFor="numeroResolucion"
                    className={`${styles.modalRevertLabel} text-required`}
                  >
                    Número de resolución
                  </label>
                  <Field
                    id="numeroResolucion"
                    name="numeroResolucion"
                    type="text"
                    maxLength={MAX_TEXT_LENGTH}
                    placeholder="Ingresa el número de resolución"
                    className={`${styles.modalRevertInput} ${
                      formik.touched.numeroResolucion &&
                      formik.errors.numeroResolucion
                        ? styles.modalRevertInputError
                        : ""
                    }`}
                    data-testid="input-numero-resolucion"
                  />
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.touched.numeroResolucion &&
                      formik.errors.numeroResolucion
                        ? formik.errors.numeroResolucion
                        : "\u00A0"}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label
                    htmlFor="td"
                    className={`${styles.modalRevertLabel} text-required`}
                  >
                    TD
                  </label>
                  <Field
                    id="td"
                    name="td"
                    type="text"
                    maxLength={MAX_TEXT_LENGTH}
                    placeholder="Ingresa el TD"
                    className={`${styles.modalRevertInput} ${
                      formik.touched.td && formik.errors.td
                        ? styles.modalRevertInputError
                        : ""
                    }`}
                    data-testid="input-td"
                  />
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.touched.td && formik.errors.td
                        ? formik.errors.td
                        : "\u00A0"}
                    </span>
                    <span className={styles.modalRevertCounter}>
                      {`${formik.values.td?.length ?? 0}/${MAX_TEXT_LENGTH}`}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={styles.modalRevertItem}>
                  <label
                    htmlFor="rubro"
                    className={`${styles.modalRevertLabel} text-required`}
                  >
                    Rubro
                  </label>
                  <Field
                    id="rubro"
                    name="rubro"
                    type="text"
                    maxLength={MAX_TEXT_LENGTH}
                    placeholder="Ingresa el rubro"
                    className={`${styles.modalRevertInput} ${
                      formik.touched.rubro && formik.errors.rubro
                        ? styles.modalRevertInputError
                        : ""
                    }`}
                    data-testid="input-rubro"
                  />
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.touched.rubro && formik.errors.rubro
                        ? formik.errors.rubro
                        : "\u00A0"}
                    </span>
                    <span className={styles.modalRevertCounter}>
                      {`${formik.values.rubro?.length ?? 0}/${MAX_TEXT_LENGTH}`}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={4} sx={{ paddingTop: "1px !important" }}>
                <div className={styles.modalRevertItem}>
                  <label
                    htmlFor="fechaPago"
                    className={`${styles.modalRevertLabel} text-required`}
                  >
                    Fecha de pago
                  </label>
                  <Field
                    id="fechaPago"
                    name="fechaPago"
                    type="date"
                    className={`${styles.modalRevertInput} ${
                      formik.touched.fechaPago && formik.errors.fechaPago
                        ? styles.modalRevertInputError
                        : ""
                    }`}
                    data-testid="input-fecha-pago"
                  />
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.touched.fechaPago && formik.errors.fechaPago
                        ? formik.errors.fechaPago
                        : "\u00A0"}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={4} sx={{ paddingTop: "1px !important" }}>
                <div className={styles.modalRevertItem}>
                  <label className={styles.modalRevertLabel}>
                    Documento resolución
                    <Tooltip
                      title="Solo se admiten archivos PDF con un tamaño máximo de 10 MB"
                      arrow
                    >
                      <InfoIcon
                        sx={{
                          fontSize: 14,
                          color: "#666",
                        }}
                      />
                    </Tooltip>
                  </label>
                  <div className={styles.modalRevertUpload}>
                    <input
                      ref={resolutionFileRef}
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        formik.setFieldTouched(
                          "documentoResolucion",
                          true,
                          false,
                        );
                        formik.setFieldValue("documentoResolucion", file, true);
                      }}
                      data-testid="input-documento-resolucion"
                    />
                    {formik.values.documentoResolucion ? (
                      <div className={styles.modalRevertFileInfo}>
                        <Typography
                          component="span"
                          className={styles.modalRevertFileName}
                        >
                          {formik.values.documentoResolucion.name.length > 25
                            ? `${formik.values.documentoResolucion.name.substring(0, 25)}...`
                            : formik.values.documentoResolucion.name}
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            formik.setFieldValue("documentoResolucion", null);
                            if (resolutionFileRef.current)
                              resolutionFileRef.current.value = "";
                          }}
                          className={styles.modalRevertRemoveFile}
                          data-testid="btn-remove-documento-resolucion"
                        >
                          Quitar
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => resolutionFileRef.current?.click()}
                        className={styles.modalRevertUploadButton}
                        data-testid="btn-upload-documento-resolucion"
                      >
                        Seleccionar archivo
                      </Button>
                    )}
                  </div>
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.errors.documentoResolucion
                        ? formik.errors.documentoResolucion
                        : "\u00A0"}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={4} sx={{ paddingTop: "1px !important" }}>
                <div className={styles.modalRevertItem}>
                  <label className={`${styles.modalRevertLabel} text-required`}>
                    Documento de pago
                    <Tooltip
                      title="Solo se admiten archivos PDF con un tamaño máximo de 10 MB"
                      arrow
                    >
                      <InfoIcon
                        sx={{
                          fontSize: 14,
                          color: "#666",
                        }}
                      />
                    </Tooltip>
                  </label>
                  <div className={styles.modalRevertUpload}>
                    <input
                      ref={paymentFileRef}
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        formik.setFieldTouched("documentoPago", true, false);
                        formik.setFieldValue("documentoPago", file, true);
                      }}
                      data-testid="input-documento-pago"
                    />
                    {formik.values.documentoPago ? (
                      <div className={styles.modalRevertFileInfo}>
                        <Typography
                          component="span"
                          className={styles.modalRevertFileName}
                        >
                          {formik.values.documentoPago.name.length > 25
                            ? `${formik.values.documentoPago.name.substring(0, 25)}...`
                            : formik.values.documentoPago.name}
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            formik.setFieldValue("documentoPago", null);
                            if (paymentFileRef.current)
                              paymentFileRef.current.value = "";
                          }}
                          className={styles.modalRevertRemoveFile}
                          data-testid="btn-remove-documento-pago"
                        >
                          Quitar
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => paymentFileRef.current?.click()}
                        className={`${styles.modalRevertUploadButton} single-column`}
                        data-testid="btn-upload-documento-pago"
                      >
                        Seleccionar archivo
                      </Button>
                    )}
                  </div>
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.touched.documentoPago &&
                      formik.errors.documentoPago
                        ? formik.errors.documentoPago
                        : "\u00A0"}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={styles.containerButtons}>
                  <Button
                    className={styles.buttonCancel}
                    onClick={() => {
                      onClose();
                      formik.resetForm();
                    }}
                    data-testid="button-cancel"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="button-submit"
                    variant="contained"
                    disabled={
                      isSubmitting ||
                      Boolean(cdpError || rpcError) ||
                      !formik.isValid ||
                      Object.keys(formik.touched).length === 0
                    }
                    className={`${styles.authorizationBtnFilters} ${styles.modalRevertSubmit}`}
                  >
                    {isSubmitting ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};
